import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Recipe, RecipeStep } from '../models/Recipe';
import { AgentProgressCallback } from '../types/agentTypes';
import recipeService from '../services/recipeService';
import recipeAnalysisService from '../services/recipeAnalysisService';
import openaiService from '../services/openaiService';
import { getApiKey } from '../services/authService';
import { useAuth } from './AuthContext';
import { QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';
import { logger } from '../utils/logger';

interface RecipeContextType {
  recipes: Recipe[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  selectedRecipe: Recipe | null;
  currentStep: number;
  hasMore: boolean;
  totalRecipes: number;
  sortBy: 'title' | 'ingredients_count';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  favorites: string[];
  onlyFavorites: boolean;
  fetchRecipes: () => Promise<void>;
  loadMoreRecipes: () => Promise<void>;
  setSort: (sortBy: 'title' | 'ingredients_count', sortOrder: 'asc' | 'desc') => void;
  setSearch: (query: string) => void;
  setOnlyFavorites: (show: boolean) => void;
  toggleFavorite: (id: string) => Promise<void>;
  getRecipeById: (id: string) => Promise<Recipe | null>;
  addRecipe: (recipe: Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>, onProgress?: AgentProgressCallback) => Promise<Recipe>;
  updateRecipe: (recipe: Recipe) => Promise<Recipe>;
  deleteRecipe: (id: string) => Promise<boolean>;
  setSelectedRecipe: (recipe: Recipe | null) => void;
  setCurrentStep: (step: number) => void;
  generateRecipeSteps: (recipe: Recipe) => Promise<Recipe>;
  generateStepImage: (step: RecipeStep, recipeTitle: string) => Promise<RecipeStep>;
  generateStepAudio: (step: RecipeStep) => Promise<RecipeStep>;
  extractRecipeFromImage: (imageFile: File) => Promise<Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>>;
  askQuestionAboutStep: (recipe: Recipe, stepIndex: number, question: string) => Promise<{ response: string; revisedSteps?: RecipeStep[] }>;
}

const RecipeContext = createContext<RecipeContextType>({
  recipes: [],
  loading: true,
  loadingMore: false,
  error: null,
  selectedRecipe: null,
  currentStep: 0,
  hasMore: false,
  totalRecipes: 0,
  sortBy: 'title',
  sortOrder: 'asc',
  searchQuery: '',
  favorites: [],
  onlyFavorites: false,
  fetchRecipes: async () => {},
  loadMoreRecipes: async () => {},
  setSort: () => {},
  setSearch: () => {},
  setOnlyFavorites: () => {},
  toggleFavorite: async () => {},
  getRecipeById: async () => null,
  addRecipe: async () => ({ 
    id: '', 
    title: '', 
    ingredients: '', 
    notes: '', 
    userId: '', 
    createdAt: Timestamp.now(), 
    updatedAt: Timestamp.now(), 
    isFavorite: false 
  } as unknown as Recipe),
  updateRecipe: async () => ({ 
    id: '', 
    title: '', 
    ingredients: '', 
    notes: '', 
    userId: '', 
    createdAt: Timestamp.now(), 
    updatedAt: Timestamp.now(), 
    isFavorite: false 
  } as unknown as Recipe),
  deleteRecipe: async () => false,
  setSelectedRecipe: () => {},
  setCurrentStep: () => {},
  generateRecipeSteps: async () => ({ 
    id: '', 
    title: '', 
    ingredients: '', 
    notes: '', 
    userId: '', 
    createdAt: Timestamp.now(), 
    updatedAt: Timestamp.now(), 
    isFavorite: false 
  } as unknown as Recipe),
  generateStepImage: async () => ({ id: '', content: '' }),
  generateStepAudio: async () => ({ id: '', content: '' }),
  extractRecipeFromImage: async () => ({ 
    title: '', 
    ingredients: '', 
    notes: ''
  }),
  askQuestionAboutStep: async () => ({ response: '' }),
});

interface RecipeProviderProps {
  children: ReactNode;
}

export const RecipeProvider: React.FC<RecipeProviderProps> = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();  // Get auth state

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalRecipes, setTotalRecipes] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'title' | 'ingredients_count'>('title');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [favorites, setFavorites] = useState<string[]>([]);  // Deprecated - kept for backward compatibility
  const [onlyFavorites, setOnlyFavoritesState] = useState<boolean>(false);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<QueryDocumentSnapshot | null>(null);  // Firestore cursor
  const RECIPES_PER_PAGE = 12;

  // Clear recipes on logout for security
  useEffect(() => {
    if (!isAuthenticated) {
      setRecipes([]);
      setSelectedRecipe(null);
      setLastVisibleDoc(null);
      setPage(1);
      setFavorites([]);
    }
  }, [isAuthenticated]);

  const fetchRecipes = useCallback(async (): Promise<void> => {
    if (!currentUser) {
      logger.warn('Cannot fetch recipes: user not authenticated');
      setRecipes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPage(1);

      const { recipes: fetchedRecipes, total, lastDoc } = await recipeService.getRecipes(
        currentUser.uid,  // Pass userId
        1,
        RECIPES_PER_PAGE,
        sortBy === 'ingredients_count' ? 'createdAt' : sortBy,  // Map to Firestore-compatible field
        sortOrder,
        searchQuery,
        onlyFavorites,
        undefined  // No cursor for initial load
      );

      setRecipes(fetchedRecipes);
      setTotalRecipes(total);
      setLastVisibleDoc(lastDoc);  // Store cursor for pagination
      setHasMore(lastDoc !== null && fetchedRecipes.length === RECIPES_PER_PAGE);
    } catch (err) {
      setError('Failed to fetch recipes');
      logger.error('Error fetching recipes', err as Error);
      setRecipes([]);
      setTotalRecipes(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [currentUser, sortBy, sortOrder, searchQuery, onlyFavorites]);

  const loadMoreRecipes = useCallback(async (): Promise<void> => {
    if (loadingMore || !hasMore || !currentUser) {
      return;
    }

    try {
      setLoadingMore(true);
      const nextPage = page + 1;

      const { recipes: newRecipes, total, lastDoc } = await recipeService.getRecipes(
        currentUser.uid,  // Pass userId
        nextPage,
        RECIPES_PER_PAGE,
        sortBy === 'ingredients_count' ? 'createdAt' : sortBy,
        sortOrder,
        searchQuery,
        onlyFavorites,
        lastVisibleDoc || undefined  // Use cursor for pagination
      );

      setRecipes(prev => [...prev, ...newRecipes]);
      setPage(nextPage);
      setTotalRecipes(total);
      setLastVisibleDoc(lastDoc);  // Update cursor

      // Check if we've loaded all recipes
      setHasMore(lastDoc !== null && newRecipes.length === RECIPES_PER_PAGE);
    } catch (err) {
      logger.error('Error loading more recipes', err as Error);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, sortBy, sortOrder, searchQuery, onlyFavorites, currentUser, lastVisibleDoc]);

  const setSort = useCallback((newSortBy: 'title' | 'ingredients_count', newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const setSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const setOnlyFavorites = useCallback((show: boolean) => {
    setOnlyFavoritesState(show);
  }, []);

  const toggleFavorite = useCallback(async (id: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('Must be logged in to favorite recipe');
    }

    const recipe = recipes.find(r => r.id === id);
    if (!recipe) {
      logger.warn(`Recipe ${id} not found in local state`);
      return;
    }

    try {
      // Toggle favorite in Firestore
      const newStatus = await recipeService.toggleFavorite(
        currentUser.uid,
        id,
        recipe.isFavorite
      );

      // Update local state optimistically
      setRecipes(prev => prev.map(r =>
        r.id === id ? { ...r, isFavorite: newStatus } : r
      ));

      if (selectedRecipe?.id === id) {
        setSelectedRecipe({ ...selectedRecipe, isFavorite: newStatus });
      }

      // Update deprecated favorites list for backward compatibility
      if (newStatus) {
        setFavorites(prev => [...prev, id]);
      } else {
        setFavorites(prev => prev.filter(fid => fid !== id));
      }
    } catch (error) {
      logger.error('Error toggling favorite', error as Error);
      throw error;
    }
  }, [currentUser, recipes, selectedRecipe]);

  // Fetch recipes on mount and when sort, search, or favorites filter changes
  useEffect(() => {
    // Debounce search if needed, but for now relying on useEffect dependency
    // Ideally use useDebounce hook for search query to prevent too many fetches
    const timeoutId = setTimeout(() => {
      fetchRecipes();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [fetchRecipes, sortBy, sortOrder, searchQuery, onlyFavorites]);

  const getRecipeById = async (id: string): Promise<Recipe | null> => {
    if (!currentUser) {
      throw new Error('Must be logged in to get recipe');
    }

    try {
      return await recipeService.getRecipeById(currentUser.uid, id);
    } catch (err) {
      setError(`Failed to fetch recipe with ID ${id}`);
      logger.error(`Error fetching recipe with ID ${id}`, err as Error);
      return null;
    }
  };

  const addRecipe = async (
    recipe: Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>,
    onProgress?: AgentProgressCallback
  ): Promise<Recipe> => {
    if (!currentUser) {
      throw new Error('Must be logged in to add recipe');
    }
    try {
      // Ensure we have an API key before using agent service
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      // Use agent service to create complete recipe from user input
      const agentService = (await import('../services/agentService')).default;

      const completeRecipe = await agentService.createRecipe(
        {
          title: recipe.title,
          ingredients: recipe.ingredients,
          notes: recipe.notes,
        },
        onProgress
      );

      // Set user ID and timestamps (createRecipe creates them, but we need to set userId)
      completeRecipe.userId = currentUser.uid;

      // Save complete recipe to Firestore (single write)
      const savedRecipe = await recipeService.addRecipe(currentUser.uid, completeRecipe);

      // Update local state
      setRecipes([...recipes, savedRecipe]);

      // Background analysis (optional metadata enhancement)
      recipeAnalysisService.analyzeRecipe(savedRecipe)
        .then(analysis => {
          const updated = { ...savedRecipe, analysis };
          recipeService.updateRecipe(currentUser.uid, updated);
          setRecipes(prev => prev.map(r => r.id === updated.id ? updated : r));
        })
        .catch(err => {
          console.warn('Background analysis failed:', err);
        });

      return savedRecipe;
    } catch (err) {
      setError('Failed to add recipe');
      logger.error('Error adding recipe', err as Error);
      throw err;
    }
  };

  const updateRecipe = async (recipe: Recipe): Promise<Recipe> => {
    if (!currentUser) {
      throw new Error('Must be logged in to update recipe');
    }

    try {
      const updatedRecipe = await recipeService.updateRecipe(currentUser.uid, recipe);
      setRecipes(recipes.map(r => (r.id === recipe.id ? updatedRecipe : r)));

      // Update selected recipe if it's the one being updated
      if (selectedRecipe && selectedRecipe.id === recipe.id) {
        setSelectedRecipe(updatedRecipe);
      }

      return updatedRecipe;
    } catch (err) {
      setError('Failed to update recipe');
      logger.error('Error updating recipe', err as Error);
      throw err;
    }
  };

  const deleteRecipe = async (id: string): Promise<boolean> => {
    if (!currentUser) {
      throw new Error('Must be logged in to delete recipe');
    }

    try {
      const success = await recipeService.deleteRecipe(currentUser.uid, id);
      if (success) {
        setRecipes(recipes.filter(r => r.id !== id));

        // Clear selected recipe if it's the one being deleted
        if (selectedRecipe && selectedRecipe.id === id) {
          setSelectedRecipe(null);
        }
      }
      return success;
    } catch (err) {
      setError('Failed to delete recipe');
      logger.error('Error deleting recipe', err as Error);
      return false;
    }
  };

  const generateRecipeSteps = async (recipe: Recipe): Promise<Recipe> => {
    try {
      // Ensure we have an API key before using agent service
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      // Use agent service to generate steps for existing recipe
      const agentService = (await import('../services/agentService')).default;
      const recipeWithSteps = await agentService.createRecipe({
        title: recipe.title,
        ingredients: recipe.ingredients,
        notes: recipe.notes,
      });

      // Preserve the original recipe ID and metadata
      recipeWithSteps.id = recipe.id;
      recipeWithSteps.userId = recipe.userId;
      recipeWithSteps.createdAt = recipe.createdAt;
      recipeWithSteps.updatedAt = Timestamp.now();
      recipeWithSteps.isFavorite = recipe.isFavorite;
      if (recipe.image) {
        recipeWithSteps.image = recipe.image;
      }

      // Update recipes list
      setRecipes(recipes.map(r => (r.id === recipe.id ? recipeWithSteps : r)));

      // Update selected recipe if it's the one being updated
      if (selectedRecipe && selectedRecipe.id === recipe.id) {
        setSelectedRecipe(recipeWithSteps);
      }

      return recipeWithSteps;
    } catch (err) {
      setError('Failed to generate recipe steps');
      console.error('Error generating recipe steps:', err);
      throw err;
    }
  };

  const generateStepImage = async (step: RecipeStep, recipeTitle: string): Promise<RecipeStep> => {
    try {
      return await recipeService.generateStepImage(step, recipeTitle);
    } catch (err) {
      setError('Failed to generate step image');
      console.error('Error generating step image:', err);
      throw err;
    }
  };

  const generateStepAudio = async (step: RecipeStep): Promise<RecipeStep> => {
    try {
      return await recipeService.generateStepAudio(step);
    } catch (err) {
      setError('Failed to generate step audio');
      console.error('Error generating step audio:', err);
      throw err;
    }
  };

  const extractRecipeFromImage = async (imageFile: File): Promise<Omit<Recipe, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'isFavorite'>> => {
    try {
      return await recipeService.extractRecipeFromImage(imageFile);
    } catch (err) {
      setError('Failed to extract recipe from image');
      console.error('Error extracting recipe from image:', err);
      throw err;
    }
  };

  const askQuestionAboutStep = async (
    recipe: Recipe, 
    stepIndex: number, 
    question: string
  ): Promise<{ response: string; revisedSteps?: RecipeStep[] }> => {
    try {
      return await recipeService.askQuestionAboutStep(recipe, stepIndex, question);
    } catch (err) {
      setError('Failed to get answer to your question');
      console.error('Error asking question about step:', err);
      throw err;
    }
  };

  const value = useMemo(
    () => ({
      recipes,
      loading,
      loadingMore,
      error,
      selectedRecipe,
      currentStep,
      hasMore,
      totalRecipes,
      sortBy,
      sortOrder,
      searchQuery,
      favorites,
      onlyFavorites,
      fetchRecipes,
      loadMoreRecipes,
      setSort,
      setSearch,
      setOnlyFavorites,
      toggleFavorite,
      getRecipeById,
      addRecipe,
      updateRecipe,
      deleteRecipe,
      setSelectedRecipe,
      setCurrentStep,
      generateRecipeSteps,
      generateStepImage,
      generateStepAudio,
      extractRecipeFromImage,
      askQuestionAboutStep,
    }),
    [
      recipes, 
      loading, 
      loadingMore, 
      error, 
      selectedRecipe, 
      currentStep, 
      hasMore, 
      totalRecipes,
      sortBy,
      sortOrder,
      searchQuery,
      favorites,
      onlyFavorites,
      fetchRecipes, 
      loadMoreRecipes,
      setSort,
      setSearch,
      setOnlyFavorites,
      toggleFavorite
    ]
  );

  return <RecipeContext.Provider value={value}>{children}</RecipeContext.Provider>;
};

export const useRecipes = () => useContext(RecipeContext);

export default RecipeContext;
